import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useIsomorphicLayoutEffect } from 'swr/_internal';

interface LoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  loadingCount: number;
  incrementLoading: () => void;
  decrementLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loadingCount, setLoadingCount] = useState(0);

  const incrementLoading = () => {
    setLoadingCount((prev) => prev + 1);
  };

  const decrementLoading = () => {
    setLoadingCount((prev) => Math.max(0, prev - 1));
  };

  const setLoading = (loading: boolean) => {
    if (loading) {
      incrementLoading();
    } else {
      decrementLoading();
    }
  };

  const isLoading = loadingCount > 0;

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        setLoading,
        loadingCount,
        incrementLoading,
        decrementLoading,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

